import React, { useState, useEffect } from "react";
import ResourceForm from "../ResourceForm/ResourceForm";
import ResourceTable from "../Resource-Table/ResourceTable";
import apiClientAdmin from "../../../services/apiClientAdmin";
import ErrorDialog from "../ErrorDialog/ErrorDialog";
import Dialog from "../FormDialog/Dialog";
import { getHeaders } from "../../ApiHeaders/apiHeaders";
import LoadingStyle from "../../Loading/Loading";
import { datadogRum } from "@datadog/browser-rum";
import { SplitDivider, useResizableSplit } from "../../SplitPane/ResizableSplit";


interface ResourceSetupProps {
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  goToPIScreen: (resourceName?: string) => void;
  canDelete?: boolean;
}


const ResourceSetup: React.FC<ResourceSetupProps> = ({
  registerTabSwitchGuard,
  goToPIScreen,
  canDelete = false,
}) => {
  const [resources, setResources] = useState<any[]>([]);
  const [selectedResource, setSelectedResource] = useState<any | null>(null);
  const [selectedAsset, setSelectedAsset] = useState<{
    asset_id: number;
    asset_name: string;
  } | null>(null);
  const [selectedRow, setSelectedRow] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<{ title: string; details: string } | null>(
    null
  );
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);

  const [dialog, setDialog] = useState<{
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    onConfirm: () => {},
  });

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });
  };

  useEffect(() => {
    if (registerTabSwitchGuard) {
      registerTabSwitchGuard(() => {
        return new Promise<boolean>((resolve) => {
          if (isFormDirty || isCreateMode) {
            setDialog({
              open: true,
              title: "Unsaved Changes",
              message:
                "You have unsaved changes. Are you sure you want to discard them?",
              confirmButtonText: "Yes",
              cancelButtonText: "No",
              onConfirm: () => {
                setIsFormDirty(false);
                setIsCreateMode(false);
                closeDialog();
                resolve(true);
              },
              onCancel: () => {
                closeDialog();
                resolve(false);
              },
            });
          } else {
            resolve(true);
          }
        });
      });
    }
  }, [isFormDirty, isCreateMode]);
  // const handleApiError = (err: any, fallbackTitle: string) => {
  //   let title = fallbackTitle;
  //   let details = 'An unknown error occurred';

  //   if (err.response?.data?.error) {
  //     title = err.response.data.error.title || fallbackTitle;
  //     details = err.response.data.error.error.message || details;
  //   } else if (err.message) {
  //     if (err.message.includes('Network Error') || err.message.includes('timeout')) {
  //       title = 'Network Error';
  //       details = 'Unable to connect to the server. Check your internet connection.';
  //     } else {
  //       details = err.message;
  //     }
  //   }
  //   setError({ title, details });
  //   datadogRum.addError(err, { title, details });
  // };

  const handleApiError = (err: any, fallbackTitle: string) => {
    let title = fallbackTitle;
    let details = "An unknown error occurred";

    const statusCode = err.response?.status;
    const errorResponse = err.response?.data?.error;

    const backendMessage =
      errorResponse?.error?.message || errorResponse?.message;

    switch (statusCode) {
      case 400:
        title = "Bad Request";
        details = backendMessage || "The data you submitted is invalid.";
        break;
      case 401:
        title = "Acces Denied";
        details =
          "You're not authorized to access this content. Please log in or check your access permissions.";
        break;
      case 403:
        title = "Access Denied";
        details = "You need permission to access this site.";
        break;
      case 404:
        title = "Not Found";
        details =
          backendMessage || "The requested resource could not be found.";
        break;
      case 500:
        title = "Internal Server Error";
        details =
          backendMessage ||
          "Server error while fetching data. Please try again later.";
        break;
      default:
        if (
          err.message?.includes("Network Error") ||
          err.message?.includes("timeout")
        ) {
          title = "Network Error";
          details =
            "Unable to connect to the server. Check your internet connection.";
        } else {
          title = fallbackTitle;
          details =
            backendMessage || err.message || "An unexpected error occurred.";
        }
        break;
    }

    setError({ title, details });
    datadogRum.addError(err, { title, details });
  };

  const fetchResourcesForAllAssets = async () => {
    const headers = await getHeaders();
    setLoading(true);
    try {
      const allResources: any[] = [];
      let page = 1;
      let hasMorePages = true;
      while (hasMorePages) {
        try {
          const response = await apiClientAdmin.get(
            `/resources/?page=${page}&page_size=200`,
            { headers }
          );
          const resourcesForAsset = response.data.response.data;

          if (resourcesForAsset.length > 0) {
            const normalized = resourcesForAsset.map((res: any) => ({
              ...res,
              id: res.resource_id,
            }));
            allResources.push(...normalized);
          }

          hasMorePages = response.data.response.meta?.next_page != null;
          page++;
        } catch (err) {
          break;
        }
      }

      setResources(allResources);
    } catch (err) {
      handleApiError(err, "Failed to fetch resources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResourcesForAllAssets();
  }, []);

  const handleCreateResource = async (newResource: any) => {
    console.log("Selected Asset in Create Resource:", selectedAsset);
    if (!selectedAsset) {
      setDialog({
        open: true,
        title: "Error",
        message: "Please select an asset before creating a resource.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
      return;
    }

    const headers = await getHeaders();
    try {
      const resourceWithAssetId = {
        ...newResource,
        asset_name: selectedAsset.asset_name,
      };

      datadogRum.startView("Create Resource");

      const response = await apiClientAdmin.post(
        `/resources/${selectedAsset.asset_id}`,
        resourceWithAssetId,
        { headers }
      );

      const createdResource = response.data.response.data;
      await fetchResourcesForAllAssets();

      // setResources([...resources, createdResource]);
      setSelectedResource(null);
      setSelectedRow(null);
      setDialog({
        open: true,
        title: "Success",
        // message: `Resource "${newResource.resource_type_code}" successfully created.`,
        message: `Resource "${newResource.iso_resource_name}" for asset "${selectedAsset.asset_name}" has been successfully created.`,
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
    } catch (err) {
      handleApiError(err, "Error creating resource");
    }
  };

  const handleSaveResource = async (updatedResource: any) => {
    const headers = await getHeaders();
    const resourceId = updatedResource.resource_id || updatedResource.id;
    const assetId = selectedResource?.asset_id;
    console.log("Updated Resource ID:", resourceId);

    if (!resourceId) {
      handleApiError(new Error("Missing resource_id"), "Invalid Resource");
      return;
    }

    try {
      datadogRum.startView("Update Resource");
      const response = await apiClientAdmin.put(
        `/resources/${assetId}/${resourceId}`,
        updatedResource,
        { headers }
      );

      if (response.status === 200) {
        await fetchResourcesForAllAssets();
        const normalized = { ...updatedResource, id: resourceId };
        setResources((prev) =>
          prev.map((resource) =>
            resource.id === resourceId ? normalized : resource
          )
        );
        setSelectedResource(null);
        setSelectedRow(null);
      }

      setDialog({
        open: true,
        title: "Success",
        message: `Resource "${updatedResource.iso_resource_name || updatedResource.resource_type_code}" successfully updated.`,
        confirmButtonText: "Close",
        onConfirm: () => {
          closeDialog();
        },
      });
    } catch (err) {
      handleApiError(err, "Error Updating resource");
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    const resource = resources.find((r) => r.resource_id === resourceId || r.id === resourceId);
    const assetId = resource?.asset_id;
    try {
      await apiClientAdmin.delete(`/resources/${assetId}/${resourceId}`);
      setResources(resources.filter((res) => res.id !== resourceId));
      setSelectedResource(null);
      setSelectedRow(null);
    } catch (err) {
      handleApiError(err, "Error deleting resource");
    }
  };

  // const handleRowSelect = (resource: any) => {
  //   setSelectedResource(resource);
  //   setSelectedRow(resource.betm_object_id);
  // };
  const handleRowSelect = (resource: any) => {
    if (isFormDirty || isCreateMode) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setIsCreateMode(false);
          setSelectedResource(resource);
          setSelectedRow(resource.resource_id);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedResource(resource);
      setSelectedRow(resource.resource_id);
    }
  };

  // const onClearSelection = () => {
  //   setSelectedResource(null);
  //   setSelectedRow(null);
  // };
  const onClearSelection = () => {
    if (isFormDirty) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        // confirmButtonText: "Discard Changes",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setSelectedResource(null);
          setSelectedRow(null);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedResource(null);
      setSelectedRow(null);
    }
  };

  const split = useResizableSplit("lg");
  return (
    <>
      {/* <div className="flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4"> */}
      <div ref={split.containerRef} style={split.containerStyle} className={`flex flex-col lg:flex-row bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}>

        {/* <div className="flex-grow lg:basis-1/2 flex flex-col relative"> */}
        <div className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}>
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
              <LoadingStyle />
            </div>
          )}
          <ResourceTable
            resources={resources}
            onDeleteResource={handleDeleteResource}
            onEditResource={handleSaveResource}
            onRowSelect={handleRowSelect}
            onClearSelection={onClearSelection}
            selectedRow={selectedRow}
          />
        </div>

        {/* <div className="flex-grow lg:basis-1/6 flex flex-col h-full xl:flex-row xl:items-stretch xl:h-auto"> */}
        {/* <div className="flex-grow flex flex-col lg:basis-1/4 xl:self-stretch"> */}
        <SplitDivider {...split.dividerProps} />
        <div className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 xl:self-stretch bg-white rounded-xl shadow-md">

          <ResourceForm
            onCreateResource={handleCreateResource}
            onSaveResource={handleSaveResource}
            onAssetChange={(asset) => setSelectedAsset(asset)}
            selectedResource={selectedResource}
            onClearSelection={onClearSelection}
            setSelectedResource={setSelectedResource}
            onDirtyChange={setIsFormDirty}
            onModeChange={(mode) => setIsCreateMode(mode === "create")}
            goToPIScreen={goToPIScreen}
            onDeleteResource={handleDeleteResource}
            canDelete={canDelete}
          />
        </div>
      </div>

      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          onConfirm={dialog.onConfirm}
          cancelButtonText={dialog.cancelButtonText}
          onCancel={dialog.onCancel}
          onClose={closeDialog}
        />
      )}
      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </>
  );
};

export default ResourceSetup;
